'use client'

import { type RefObject, useEffect, useState } from 'react'

// The sticky rail is h-16; the hand-over happens at its bottom edge.
export const RAIL_PX = 64

// 'entering' is the door's top on screen with its bottom still under the
// fold — the short laptop's rest state (spec §6): the rail holds the
// door until the hero's is wholly on the page.
export type DoorPlace = 'below' | 'entering' | 'visible' | 'above'

// Where a door sits against the viewport below the rail. Pure.
export function placeOf(top: number, bottom: number, vh: number): DoorPlace {
    if (bottom <= RAIL_PX) return 'above'
    if (top >= vh) return 'below'
    if (bottom > vh) return 'entering'
    return 'visible'
}

// Any part of the door on the page below the rail.
export const onScreen = (place: DoorPlace): boolean =>
    place === 'visible' || place === 'entering'

// Hands `connect` an element by id as soon as it exists. The doors are
// rendered by the dynamic door island, which streams in after the shell,
// so an effect that looks the element up once can run before it has
// arrived and never watch it. Returns a cleanup for both the wait and
// whatever `connect` returned.
export function whenPresent(
    id: string,
    connect: (el: Element) => () => void
): () => void {
    let cleanup: (() => void) | null = null
    const now = document.getElementById(id)
    if (now) {
        cleanup = connect(now)
        return () => cleanup?.()
    }
    const mo = new MutationObserver(() => {
        const el = document.getElementById(id)
        if (!el) return
        mo.disconnect()
        cleanup = connect(el)
    })
    mo.observe(document.body, { childList: true, subtree: true })
    return () => {
        mo.disconnect()
        cleanup?.()
    }
}

// Tracks a door's place on every scroll frame. Measured, not observed:
// an IntersectionObserver reports only changes of intersection, so a
// door that starts below the viewport (the hero's, on phones) and leaves
// above it in one fling never reports at all.
export function useDoorPlace(id: string): DoorPlace {
    const [place, setPlace] = useState<DoorPlace>('below')
    useEffect(
        () =>
            whenPresent(id, (el) => {
                let raf = 0
                const measure = () => {
                    raf = 0
                    const r = el.getBoundingClientRect()
                    setPlace(placeOf(r.top, r.bottom, window.innerHeight))
                }
                const schedule = () => {
                    if (!raf) raf = requestAnimationFrame(measure)
                }
                measure()
                window.addEventListener('scroll', schedule, { passive: true })
                window.addEventListener('resize', schedule)
                return () => {
                    window.removeEventListener('scroll', schedule)
                    window.removeEventListener('resize', schedule)
                    if (raf) cancelAnimationFrame(raf)
                }
            }),
        [id]
    )
    return place
}

// Whether the hero's door (#hero-door) has scrolled above the rail's
// bottom edge. The rail door appears at that moment and the ground's
// label hides at it (spec §6: one door on screen), so both read the one
// measure.
export function useHeroDoorAbove(): boolean {
    return useDoorPlace('hero-door') === 'above'
}

// Whether the rail holds the door at rest, while the hero's is still
// under the fold (spec §6): true from xl, where the page declares it as
// a token on the element given — `xl:[--rail-holds-rest:1]` — so the
// breakpoint stays in CSS and no viewport hook enters. Re-read on
// resize, since a window can cross the breakpoint.
export function useRailHoldsRest(ref: RefObject<HTMLElement | null>): boolean {
    const [holds, setHolds] = useState(false)
    useEffect(() => {
        const read = () => {
            const el = ref.current
            if (!el) return
            setHolds(
                getComputedStyle(el)
                    .getPropertyValue('--rail-holds-rest')
                    .trim() === '1'
            )
        }
        read()
        window.addEventListener('resize', read)
        return () => window.removeEventListener('resize', read)
    }, [ref])
    return holds
}
